/**
 * 🎯 EXECUTIVE DASHBOARD GRID
 * ════════════════════════════════════════════════════════════════════════
 * 
 * FILOSOFÍA:
 * "El frontend obedece, no piensa."
 * 
 * REGLAS INMUTABLES:
 * ✅ Renderiza widgets en el orden EXACTO que vienen del backend
 * ✅ Respeta layouts (x, y, w, h) sin modificar
 * ✅ NO reordena
 * ✅ NO interpreta intent
 * ✅ NO decide narrativa
 * 
 * ❌ NO añadir lógica de:
 *    - Reordenamiento
 *    - Heurísticas de colocación
 *    - Interpretación de roles
 *    - Drag & drop (por ahora)
 * 
 * El backend ya decidió TODO. Este componente solo obedece.
 */

import React from 'react';
import './DashboardGrid.css';
import MetricWidget from './widgets/MetricWidget';
import BarChartWidget from './widgets/BarChartWidget';
import LineChartWidget from './widgets/LineChartWidget';
import TableWidget from './widgets/TableWidget';
import LazyWidget from '../../../shared/ui/LazyWidget';

/**
 * Widget con layout del backend
 */
interface DashboardWidget {
  id: string;
  type: 'metric' | 'bar' | 'line' | 'table' | 'pie';
  title: string;
  data: any[];
  layout?: {
    x: number;  // 0-11 (grid 12 columnas)
    y: number;  // 0-N
    w: number;  // 1-12
    h: number;  // 1-N (unidades de altura)
  };
  metadata?: any;
}

interface Dashboard {
  id: string;
  title: string;
  widgets: DashboardWidget[];
}

interface DashboardGridProps {
  dashboard: Dashboard;
}

/**
 * Grid que renderiza widgets en orden exacto del backend
 */
export const DashboardGrid: React.FC<DashboardGridProps> = ({ dashboard }) => {
  
  const { widgets } = dashboard;
  const visibleWidgets = widgets.filter((widget) => Array.isArray(widget.data) && widget.data.length > 0);
  const minRowHeight = 72;
  
  // Log para debugging
  console.log('📊 DashboardGrid rendering:', {
    dashboardId: dashboard.id,
    widgetCount: visibleWidgets.length,
    order: visibleWidgets.map(w => ({ id: w.id, type: w.type }))
  });
  
  return (
    <div className="dashboard-grid-container">
      {/* 
        REGLA FUNDAMENTAL:
        Renderizar en el ORDEN EXACTO que vienen.
        NO reordenar. NO interpretar.
      */}
      {visibleWidgets.map((widget, index) => {
        // Layout del widget (del backend o del objeto widget)
        const layout = widget.layout;
        const tableRowSpan = widget.type === 'table' ? getTableRowSpan(widget.data?.length ?? 0, minRowHeight) : null;
        
        // Si no hay layout, usar auto-layout simple (uno abajo del otro)
        const computedLayout = layout || {
          x: 0,
          y: index * 5,
          w: 12,
          h: widget.type === 'metric' ? 4 : widget.type === 'table' ? tableRowSpan ?? 5 : 5
        };
        
        return (
          <WidgetContainer
            key={widget.id}
            widget={widget}
            layout={computedLayout}
            index={index}
            minRowHeight={minRowHeight}
          />
        );
      })}
    </div>
  );
};

/**
 * Container de widget con layout aplicado
 */
interface WidgetContainerProps {
  widget: DashboardWidget;
  layout: NonNullable<DashboardWidget['layout']>;
  index: number;
  minRowHeight: number;
}

const WidgetContainer: React.FC<WidgetContainerProps> = ({ widget, layout, index, minRowHeight }) => {
  
  // Detectar si es PRIMARY_INSIGHT (primer widget + tipo metric)
  const isPrimary = index === 0 && widget.type === 'metric';
  const maxRowSpan = isPrimary ? 3 : (widget.type === 'metric' ? 4 : widget.type === 'table' ? 12 : 6);
  const maxColumnSpan = isPrimary ? 8 : 12;
  const tableRowSpan = widget.type === 'table' ? getTableRowSpan(widget.data?.length ?? 0, minRowHeight) : null;
  const rowSpan = widget.type === 'table' && tableRowSpan !== null
    ? Math.min(layout.h, Math.min(tableRowSpan, maxRowSpan))
    : Math.min(layout.h, maxRowSpan);
  const columnSpan = Math.min(layout.w, maxColumnSpan);
  
  // Estilos basados en layout del backend
  const gridStyle: React.CSSProperties = {
    gridColumnEnd: `span ${columnSpan}`,
    gridRowEnd: `span ${rowSpan}`,
    minHeight: `${rowSpan * minRowHeight}px`,
  };
  
  return (
    <div
      className={`widget-container ${isPrimary ? 'widget-primary' : ''}`}
      style={gridStyle}
    >
      <LazyWidget
        placeholder={<div className="widget-placeholder">Cargando widget...</div>}
      >
        <WidgetRenderer widget={widget} isPrimary={isPrimary} />
      </LazyWidget>
    </div>
  );
};

/**
 * Renderer que mapea tipo → componente
 * 
 * REGLA: Switch simple. Sin ifs por intent, role o dominio.
 */
interface WidgetRendererProps {
  widget: DashboardWidget;
  isPrimary: boolean;
}

const WidgetRenderer: React.FC<WidgetRendererProps> = ({ widget, isPrimary }) => {
  
  switch (widget.type) {
    case 'metric':
      return <MetricWidget widget={widget} isPrimary={isPrimary} />;
    
    case 'bar':
      return <BarChartWidget widget={widget} />;
    
    case 'line':
      return <LineChartWidget widget={widget} />;
    
    case 'table':
      return <TableWidget widget={widget} />;
    
    case 'pie':
      // TODO: Implementar PieChartWidget
      return <div className="widget-placeholder">Pie Chart (pendiente)</div>;
    
    default:
      return (
        <div className="widget-error">
          <p>Tipo de widget desconocido: {widget.type}</p>
        </div>
      );
  }
};

const TABLE_ROW_HEIGHT = 38;
const TABLE_HEADER_HEIGHT = 44;
const TABLE_CHROME_HEIGHT = 96;
const TABLE_MIN_ROWS = 2;
const TABLE_MAX_VISIBLE_ROWS = 10;

function getTableRowSpan(rowCount: number, minRowHeight: number): number {
  const rows = Math.max(TABLE_MIN_ROWS, rowCount);
  const visibleRows = Math.min(rows, TABLE_MAX_VISIBLE_ROWS);
  const heightPx = TABLE_CHROME_HEIGHT + TABLE_HEADER_HEIGHT + (TABLE_ROW_HEIGHT * visibleRows);
  return Math.max(2, Math.ceil(heightPx / minRowHeight));
}
